import { StructuredData } from './StructuredData';

export interface Breadcrumb {
  name: string;
  url: string;
}

interface BreadcrumbListProps {
  breadcrumbs: Breadcrumb[];
}

/**
 * BreadcrumbList - Generates BreadcrumbList structured data for search results
 *
 * Displays navigation path in Google search results:
 * Home > Articles > How It Works
 *
 * Usage:
 * <BreadcrumbList
 *   breadcrumbs={[
 *     { name: 'Home', url: 'https://trademagic.gg/' },
 *     { name: 'Articles', url: 'https://trademagic.gg/articles' },
 *     { name: 'How It Works', url: 'https://trademagic.gg/articles/how-it-works' }
 *   ]}
 * />
 */
export const BreadcrumbList: React.FC<BreadcrumbListProps> = ({
  breadcrumbs,
}) => {
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };

  return <StructuredData data={breadcrumbSchema} />;
};

/**
 * Helper function to generate breadcrumbs from URL path
 *
 * Usage:
 * const breadcrumbs = generateBreadcrumbs('/articles/how-it-works', {
 *   'articles': 'Articles',
 *   'how-it-works': 'How It Works'
 * });
 */
export function generateBreadcrumbs(
  pathname: string,
  labelMap?: Record<string, string>
): Breadcrumb[] {
  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home', url: 'https://trademagic.gg/' },
  ];

  const pathSegments = pathname.split('/').filter((segment) => segment);

  let currentPath = '';
  for (const segment of pathSegments) {
    currentPath += `/${segment}`;

    // Use label from map, or format segment (capitalize, replace hyphens)
    const label =
      labelMap?.[segment] ||
      segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    breadcrumbs.push({
      name: label,
      url: `https://trademagic.gg${currentPath}`,
    });
  }

  return breadcrumbs;
}
